<template>
    <m-pageframe title="Brand, Campaign and Toll Free Verification">
        <v-container class="py-6 px-md-8" style="max-width: 900px;">
            <!-- Header Section -->
            <div class="mb-6">
                <h1 class="text-h5 font-weight-bold mb-4">
                    Brand, Campaign and Toll Free Verification
                </h1>

                <v-alert
                    type="info"
                    outlined
                    class="mb-0"
                    icon="mdi-information-outline"
                >
                    <p class="mb-2">
                        To prevent spam and ensure message deliverability, the major cell phone carriers (also known as Mobile Network Operators, or MNOs) require registration of your organization's brand and texting campaigns through The Campaign Registry (TCR). This information is also used by the carriers to verify any toll free numbers added to your organization.
                    </p>
                    <p class="mb-0">
                        RedChirp manages this process for you using the information you provide below. We recommend completing this step as soon as possible to avoid delays in launching your texting program.
                    </p>
                </v-alert>
            </div>

            <!-- Loading State -->
            <div v-if="mixin_tcr_loading" class="text-center py-8">
                <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                />
                <p class="mt-4 grey--text">Loading verification information...</p>
            </div>

            <!-- Form Content -->
            <div v-else-if="!load_error">
                <div class="d-flex flex-wrap gap-3 align-center justify-space-between mb-4">
                    <span class="text-body-2 grey--text text--darken-1">
                        Complete all sections below to ensure successful registration
                    </span>
                    <v-chip
                        :color="mixin_is_form_valid ? 'success' : 'grey'"
                        small
                        outlined
                    >
                        <v-icon left small>
                            {{ mixin_is_form_valid ? 'mdi-check-circle' : 'mdi-progress-clock' }}
                        </v-icon>
                        {{ mixin_sections_complete }} of 5 sections complete
                    </v-chip>
                </div>

                <v-expansion-panels
                    class="d-flex flex-column gap-1 mb-6"
                    v-model="active_panel"
                    accordion
                    flat
                >
                    <!-- Organization Info Section -->
                    <v-expansion-panel>
                        <v-expansion-panel-header class="shaded-light">
                            <div class="d-flex align-center">
                                <v-icon
                                    :color="mixin_org_info_complete ? 'success' : 'grey'"
                                    class="mr-3"
                                >
                                    {{ mixin_org_info_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                                <div>
                                    <span class="font-weight-medium">Organization Info</span>
                                    <div class="text-caption grey--text">Legal company details and tax identification</div>
                                </div>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content eager>
                            <div class="pt-4">
                                <rc-base-input
                                    label="Legal Company Name"
                                    type="text"
                                    v-model="mixin_tcr_form_data.organization_legal_name"
                                    hint="This should match exactly what the IRS has on file for your company's EIN."
                                    required
                                    @validated="v => mixinOnFieldValidated('organization_legal_name', v)"
                                />
    
                                <v-checkbox
                                    label="My business has a different DBA (Doing Business As)"
                                    class="mt-2 mb-0"
                                    v-model="mixin_tcr_form_data.has_dba"
                                    hide-details
                                    @change="mixin_tcr_form_data.has_dba && $nextTick(() => $refs['dba-input']?.focus())"
                                />
    
                                <transition name="fade">
                                    <rc-base-input
                                        v-if="mixin_tcr_form_data.has_dba"
                                        ref="dba-input"
                                        label="DBA"
                                        class="mt-4"
                                        v-model="mixin_tcr_form_data.dba"
                                        hint="DBA or Brand Name, if different from legal name"
                                        required
                                        @validated="v => mixinOnFieldValidated('dba', v)"
                                    />
                                </transition>
    
                                <rc-select
                                    label="Legal Form"
                                    class="mt-4"
                                    v-model="mixin_tcr_form_data.tcr_legal_form"
                                    :items="$rc.enumChoices('tcr_organiztion_legal_form', false)"
                                    hint="Choose which best describes your organization. Most RedChirp customers choose 'Private Company'"
                                    required
                                    @validated="v => mixinOnFieldValidated('tcr_legal_form', v)"
                                />
    
                                <rc-base-input
                                    type="us-or-ca-tax-id"
                                    label="Tax Identification Number (EIN)"
                                    class="mt-4"
                                    v-model="mixin_tcr_form_data.tax_id"
                                    required
                                    @validated="v => mixinOnFieldValidated('tax_id', v)"
                                >
                                    <template #hint>
                                        <div class="d-flex align-center">
                                            <span class="mr-2">Your organization's Tax ID</span>
                                            <v-tooltip bottom>
                                                <template #activator="{ on, attrs }">
                                                    <v-icon
                                                        small
                                                        color="grey"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                    >
                                                        mdi-help-circle
                                                    </v-icon>
                                                </template>
                                                <div>
                                                    <p class="mb-0"><strong>US:</strong> Use your 9-digit Employer Identification Number (EIN)</p>
                                                    <p class="mb-0"><strong>Canada:</strong> Nine Digit Business Number (BN 9 - not BN15)</p>
                                                </div>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </rc-base-input>

                                <rc-select
                                    label="Which Best Describes Your Business Vertical? (Pick One)"
                                    v-model="mixin_tcr_form_data.tcr_business_category"
                                    :items="mixin_biz_category_choices"
                                    required
                                    @validated="v => mixinOnFieldValidated('tcr_business_category', v)"
                                />
    
                                <div class="d-flex justify-end mt-4">
                                    <v-btn
                                        color="primary"
                                        @click="active_panel = 1"
                                    >
                                        Next
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Business Contact Section -->
                    <v-expansion-panel>
                        <v-expansion-panel-header class="shaded-light">
                            <div class="d-flex align-center">
                                <v-icon
                                    :color="mixin_contact_complete ? 'success' : 'grey'"
                                    class="mr-3"
                                >
                                    {{ mixin_contact_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                                <div>
                                    <span class="font-weight-medium">Business Contact</span>
                                    <div class="text-caption grey--text">Primary contact for registration issues</div>
                                </div>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content eager>
                            <div class="pt-4">
                                <p class="text-body-2 grey--text text--darken-1 mb-4">
                                    The contact information for your organization's primary, individual contact who should be called or emailed if there is any issue with your registration.
                                </p>
    
                                <v-row class="mb-0">
                                    <v-col cols="12" sm="6">
                                        <rc-base-input
                                            label="Contact First Name"
                                            v-model="mixin_tcr_form_data.business_contact_first_name"
                                            class="mb-0"
                                            required
                                            @validated="v => mixinOnFieldValidated('business_contact_first_name', v)"
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <rc-base-input
                                            label="Contact Last Name"
                                            v-model="mixin_tcr_form_data.business_contact_last_name"
                                            class="mb-0"
                                            required
                                            @validated="v => mixinOnFieldValidated('business_contact_last_name', v)"
                                        />
                                    </v-col>
                                </v-row>
    
                                <rc-base-input
                                    type="tel"
                                    label="Contact Phone Number"
                                    class="mt-2"
                                    v-model="mixin_tcr_form_data.tcr_phone_number"
                                    hint="The Campaign Registry requires you to provide an official phone number (not a personal one)."
                                    required
                                    @validated="v => mixinOnFieldValidated('tcr_phone_number', v)"
                                />
    
                                <rc-base-input
                                    type="official-email"
                                    label="Contact Email"
                                    class="mt-4"
                                    hint="The Campaign Registry requires you to provide an official email address (not a personal one). For example, john_doe@yourcompany.com instead of john_doe@gmail.com."
                                    v-model="mixin_tcr_form_data.tcr_contact_email"
                                    required
                                    @validated="v => mixinOnFieldValidated('tcr_contact_email', v)"
                                />
    
                                <div class="d-flex justify-space-between mt-4">
                                    <v-btn
                                        plain
                                        @click="active_panel = 0"
                                    >
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Previous
                                    </v-btn>
                                    <v-btn
                                        color="primary"
                                        @click="active_panel = 2"
                                    >
                                        Next
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Business Address Section -->
                    <v-expansion-panel>
                        <v-expansion-panel-header class="shaded-light">
                            <div class="d-flex align-center">
                                <v-icon
                                    :color="mixin_address_complete ? 'success' : 'grey'"
                                    class="mr-3"
                                >
                                    {{ mixin_address_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                                <div>
                                    <span class="font-weight-medium">Business Address</span>
                                    <div class="text-caption grey--text">Official registered business address</div>
                                </div>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content eager>
                            <div class="pt-4">
                                <rc-base-input
                                    label="Street Address"
                                    v-model="mixin_tcr_form_data.business_address"
                                    hint="This should match exactly what's on file with the IRS and/or your state/province's business registration office"
                                    required
                                    @validated="v => mixinOnFieldValidated('business_address', v)"
                                />
    
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <rc-base-input
                                            label="City"
                                            v-model="mixin_tcr_form_data.business_address_city"
                                            required
                                            @validated="v => mixinOnFieldValidated('business_address_city', v)"
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <rc-base-input
                                            :label="mixin_state_or_province"
                                            v-model="mixin_tcr_form_data.business_address_state"
                                            required
                                            @validated="v => mixinOnFieldValidated('business_address_state', v)"
                                        />
                                    </v-col>
                                </v-row>
    
                                <v-row class="mt-0">
                                    <v-col cols="12" sm="6">
                                        <rc-base-input
                                            label="Zip / Postal Code"
                                            v-model="mixin_tcr_form_data.business_address_zip"
                                            required
                                            @validated="v => mixinOnFieldValidated('business_address_zip', v)"
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <rc-select
                                            label="Country"
                                            v-model="mixin_tcr_form_data.country"
                                            :items="$rc.enumChoices('tcr_country', false)"
                                            required
                                            @validated="v => mixinOnFieldValidated('country', v)"
                                        />
                                    </v-col>
                                </v-row>
    
                                <div class="d-flex justify-space-between mt-4">
                                    <v-btn
                                        plain
                                        @click="active_panel = 1"
                                    >
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Previous
                                    </v-btn>
                                    <v-btn
                                        color="primary"
                                        @click="active_panel = 3"
                                    >
                                        Next
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Website Section -->
                    <v-expansion-panel>
                        <v-expansion-panel-header class="shaded-light">
                            <div class="d-flex align-center">
                                <v-icon
                                    :color="mixin_website_complete ? 'success' : 'grey'"
                                    class="mr-3"
                                >
                                    {{ mixin_website_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                                <div>
                                    <span class="font-weight-medium">Website</span>
                                    <div class="text-caption grey--text">Your organization's primary website</div>
                                </div>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content eager>
                            <div class="pt-4">
                                <rc-base-input
                                    label="Website URL"
                                    v-model="mixin_tcr_form_data.website"
                                    hint="Your organization's primary website (e.g. for Microsoft, https://www.microsoft.com)"
                                    required
                                    @validated="v => mixinOnFieldValidated('website', v)"
                                    @blur="mixinOnUrlBlur('website')"
                                />
    
                                <div class="d-flex justify-space-between mt-4">
                                    <v-btn
                                        plain
                                        @click="active_panel = 2"
                                    >
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Previous
                                    </v-btn>
                                    <v-btn
                                        color="primary"
                                        @click="active_panel = 4"
                                    >
                                        Next
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Policies Section -->
                    <v-expansion-panel>
                        <v-expansion-panel-header class="shaded-light">
                            <div class="d-flex align-center">
                                <v-icon
                                    :color="mixin_policies_complete ? 'success' : 'grey'"
                                    class="mr-3"
                                >
                                    {{ mixin_policies_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                                <div>
                                    <span class="font-weight-medium">Policies</span>
                                    <div class="text-caption grey--text">Privacy policy and terms and conditions</div>
                                </div>
                            </div>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content eager>
                            <div class="pt-4">
                                <rc-base-input
                                    label="Privacy Policy URL"
                                    v-model="mixin_tcr_form_data.url_privacy_policy"
                                    hint="The web address (URL) for your organization's privacy policy"
                                    required
                                    @validated="v => mixinOnFieldValidated('url_privacy_policy', v)"
                                    @blur="mixinOnUrlBlur('url_privacy_policy')"
                                />
    
                                <rc-select
                                    class="mt-4"
                                    label="Privacy Policy Addendum"
                                    v-model="mixin_tcr_form_data.privacy_policy_addendum"
                                    :items="mixin_privacy_policy_addendum_options"
                                    required
                                    @validated="v => mixinOnFieldValidated('privacy_policy_addendum', v)"
                                />
    
                                <div class="text-caption mt-1 mb-4 d-flex align-center">
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on">
                                                <rc-link
                                                    :to="{ name: 'default-privacy-policy-addendum', params: { organization_id } }"
                                                    text="Privacy policy addendum template"
                                                    text_transform="capitalize"
                                                />
                                            </span>
                                        </template>
                                        <span>Our privacy policy addendum template, tailored for your organization</span>
                                    </v-tooltip>
                                    <span class="mx-2 grey--text">|</span>
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on">
                                                <rc-link
                                                    href="https://redchirp.com/texting-requirements-of-your-privacy-policy/"
                                                    text="Carrier requirements"
                                                    text_transform="capitalize"
                                                />
                                            </span>
                                        </template>
                                        <span>Examples of acceptable privacy policy language for crafting your own</span>
                                    </v-tooltip>
                                </div>
    
                                <v-divider class="my-4" />
    
                                <rc-base-input
                                    label="Terms and Conditions URL"
                                    class="mt-4"
                                    v-model="mixin_tcr_form_data.url_terms_and_conditions"
                                    hint="The web address (URL) for your organization's terms and conditions"
                                    required
                                    @validated="v => mixinOnFieldValidated('url_terms_and_conditions', v)"
                                    @blur="mixinOnUrlBlur('url_terms_and_conditions')"
                                />
    
                                <rc-select
                                    class="mt-4"
                                    label="Terms and Conditions Addendum"
                                    v-model="mixin_tcr_form_data.terms_and_conditions_addendum"
                                    :items="mixin_terms_and_conditions_addendum_options"
                                    required
                                    @validated="v => mixinOnFieldValidated('terms_and_conditions_addendum', v)"
                                />
    
                                <div class="text-caption mt-1 mb-2 d-flex align-center">
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on">
                                                <rc-link
                                                    :to="{ name: 'default-terms-and-conditions-addendum', params: { organization_id } }"
                                                    text="Terms and conditions addendum template"
                                                    text_transform="capitalize"
                                                />
                                            </span>
                                        </template>
                                        <span>Our terms and conditions addendum template, tailored for your organization</span>
                                    </v-tooltip>
                                    <span class="mx-2 grey--text">|</span>
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on">
                                                <rc-link
                                                    href="https://redchirp.com/texting-requirements-and-your-websites-terms-and-conditions/"
                                                    text="Carrier requirements"
                                                    text_transform="capitalize"
                                                />
                                            </span>
                                        </template>
                                        <span>Examples of acceptable terms and conditions language for crafting your own</span>
                                    </v-tooltip>
                                </div>

                                <div class="d-flex justify-start mt-4">
                                    <v-btn
                                        plain
                                        @click="active_panel = 3"
                                    >
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Previous
                                    </v-btn>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>

                <!-- Action Buttons -->
                <v-alert
                    v-if="mixin_is_form_valid"
                    outlined
                    type="success"
                    class="mb-6 pa-4"
                >
                    <div class="font-weight-medium">Registration Submitted Successfully!</div>
                    <div class="text-body-2 grey--text">If you need to update anything, make changes above and it will be saved automatically. Otherwise, you are all set.</div>
                </v-alert>

                <div v-else class="d-flex align-center justify-center gap-1 text-body-2 mb-6">
                    <v-icon small color="grey">mdi-information-outline</v-icon>
                    <span class="grey--text">Progress is saved automatically</span>
                </div>

                <div
                    v-if="$rc.$state.isAuthActorSystemAdmin()"
                    class="text-center"
                >
                    <v-divider class="mb-4" />

                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <rc-link
                                text="View campaign registration (TCR) information"
                                v-bind="attrs"
                                v-on="on"
                                :to="{
                                    name: 'welcome_admin_tcr_campaign_registration',
                                    params: { organization_id }
                                }"
                            />
                        </template>
                        <span>Staff Only</span>
                    </v-tooltip>
                </div>
            </div>
        </v-container>
    </m-pageframe>
</template>

<script>
import _ from 'lodash';
import TcrOrganizationSetupMixin from '@/mixins/tcr-organization-setup.js';

export default {
    name: "BrandCampaignTollFreeVerification",

    mixins: [TcrOrganizationSetupMixin],

    data() {
        return {
            load_error: false,
            active_panel: null,
            last_saved_data: null,
            was_complete_on_load: false,
        };
    },

    computed: {
        organization_id() {
            return this.mixin_organization_id;
        },

        has_unsaved_changes() {
            if (!this.last_saved_data) return false;
            return !_.isEqual(this.mixin_tcr_form_data, this.last_saved_data);
        },
    },

    watch: {
        mixin_tcr_form_data: {
            deep: true,
            handler() {
                this.debouncedSave();
            },
        },
    },

    created() {
        this.debouncedSave = _.debounce(this.autoSave, 1500);
    },

    mounted() {
        this.loadFormData();
    },

    beforeDestroy() {
        if (this.debouncedSave?.cancel) {
            this.debouncedSave.cancel();
        }
    },

    methods: {
        loadFormData() {
            this.mixinLoadTcrFormData({
                on_success: (data) => {
                    this.last_saved_data = _.cloneDeep(data || {});
                    this.was_complete_on_load = data?.complete === true;
                },
                on_error: () => {
                    this.$rc.snackbar('There was an error loading your registration information. Please try again.');
                    this.load_error = true;
                },
            });
        },
        
        autoSave() {
            if (!this.has_unsaved_changes) return;
            if (!this.mixin_organization_id) return;

            this.mixinSaveTcrFormData({
                on_success: () => {
                    this.last_saved_data = _.cloneDeep(this.mixin_tcr_form_data);
                    this.was_complete_on_load = this.mixin_is_form_valid;
                },
                on_error: () => {
                    this.loadFormData();
                },
                error_message: 'There was an error saving your registration information. Please try again.',
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    :deep(.v-expansion-panel-header) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
    
    :deep(.v-expansion-panel-content) {
        border: 1px solid #eee;
        border-radius: var(--border-radius-default);
    }
</style>